import type {
    ArchitectureNote,
    ClubEvent,
    Facility,
    GalleryItem,
    MembershipTier,
    NatureFeature,
    Stat,
    Venue
} from '@/types'

import { library, media } from '@/constants/media'

export const architectureNotes: ArchitectureNote[] = [
    {
        title: 'Rammed earth',
        description: 'Walls pressed by hand from soil excavated on site, a building method native to the Alentejo for a thousand years.'
    },
    {
        title: 'Timber canopy',
        description: 'A 64-metre roof of cross-laminated larch, cantilevered over the terrace without a single column.'
    },
    {
        title: 'Frameless glass',
        description: 'The pavilion opens fully onto the 18th green, so the course becomes the fourth wall.'
    },
    {
        title: 'Passive climate',
        description: 'Thermal mass, cross-ventilation and a solar canopy replace air conditioning entirely.'
    }
]

export const clubhouseStats: Stat[] = [
    { value: 64, suffix: ' m', label: 'timber canopy without columns' },
    { value: 1850, suffix: ' m²', label: 'of clubhouse under one roof' },
    { value: 100, suffix: '%', label: 'of daytime power from the solar canopy' },
    { value: 0, label: 'air conditioning units, by design' }
]

export const facilities: Facility[] = [
    {
        name: 'The Range',
        description: 'Forty-eight bays, half covered and heated, with ball-flight tracking on every one. Target greens stretch 320 metres and are shaped like the course’s own.',
        specs: ['48 tracked bays', '320 m range', '06:30 to 22:00'],
        image: media.academyRange
    },
    {
        name: 'Short Game Park',
        description: 'Three hectares of chipping greens, pitching areas and bunkers, built from the same sand and turf as the championship course.',
        specs: ['3 hectares', '7 practice greens', 'Course-matched sand'],
        image: media.academyShortGame
    },
    {
        name: 'Putting Lab',
        description: 'An indoor studio where laser guides and a pressure-mapped green show exactly how your stroke starts the ball rolling.',
        specs: ['Pressure-mapped green', 'Laser alignment', 'Video review'],
        image: media.academyPutting
    },
    {
        name: 'Performance Studio',
        description: '3D motion capture, launch monitoring and custom club fitting, with your analysis sent to your phone after every session.',
        specs: ['3D motion capture', 'Custom fitting', 'Session reports'],
        image: media.academyStudio
    },
    {
        name: 'The Short Nine',
        description: 'A floodlit par-three course of nine holes from 68 to 142 yards, open until eleven at night. Built for quick rounds, first rounds and late light.',
        specs: ['9 holes', '68 to 142 yds', 'Floodlit until 23:00'],
        image: media.academyShortNine
    }
]

export const lessonFocuses = [
    'Full swing',
    'Short game',
    'Putting',
    'Performance Studio assessment',
    'Custom club fitting',
    'Junior coaching'
]

export const academyStats: Stat[] = [
    { value: 48, label: 'tracked bays on the range' },
    { value: 11, label: 'PGA professionals on staff' },
    { value: 9, label: 'floodlit short-course holes' },
    { value: 6, suffix: '+', label: 'years, when junior coaching begins' }
]

export const membershipTiers: MembershipTier[] = [
    {
        name: 'Full',
        tagline: 'Every day of the year, both courses, no limits.',
        featured: true,
        benefits: [
            'Unlimited play on the Championship 18 and the Short Nine',
            'Tee times bookable 21 days ahead',
            'Unlimited range, short game park and putting lab',
            'Up to 24 accompanied guest rounds a year',
            'Member locker, bag storage and club care',
            'Invitations to every member competition'
        ]
    },
    {
        name: 'International',
        tagline: 'For members who live more than 500 km away.',
        benefits: [
            '30 rounds a year on the Championship 18',
            'Four nights a year in the Lodges',
            'Airport transfers on arrival',
            'Tee times bookable 30 days ahead',
            'Full clubhouse and dining privileges'
        ]
    },
    {
        name: 'Weekday',
        tagline: 'Monday to Thursday, from first light.',
        benefits: [
            'Unlimited weekday play on both courses',
            'Tee times bookable 7 days ahead',
            'Unlimited range access',
            'Member dining rates',
            'Two twilight guest passes a month'
        ]
    },
    {
        name: 'Academy',
        tagline: 'Built around getting better.',
        benefits: [
            'Unlimited range, short game and putting lab',
            '12 private lessons a year with a PGA professional',
            'A Performance Studio assessment every quarter',
            'Unlimited play on the Short Nine',
            'Twilight rounds on the Championship 18'
        ]
    }
]

export const venues: Venue[] = [
    {
        name: 'The Terrace',
        kind: 'All-day dining',
        description: 'Wood-fired cooking under the timber canopy, looking straight down the 18th fairway. Breakfast from the first tee time, lunch until the last group walks in.',
        details: ['07:00 to 18:00 daily', '96 seats inside and out'],
        image: media.diningTerrace,
        action: { label: 'Reserve a table', venue: 'The Terrace' }
    },
    {
        name: 'Cellar Room',
        kind: 'Tasting menu',
        description: 'Twenty-four seats beneath the clubhouse and a menu that changes with the estate garden, paired with wines from the surrounding valley.',
        details: ['Wednesday to Sunday from 19:30', '24 seats'],
        image: media.diningCellar,
        action: { label: 'Reserve a table', venue: 'Cellar Room' }
    },
    {
        name: 'Halfway House',
        kind: 'At the turn',
        description: 'A stone pavilion between the 9th green and the 10th tee for coffee, a warm bifana and something cold. Five minutes, then back out.',
        details: ['Open during play', 'Between holes 9 and 10'],
        image: media.diningHalfway
    },
    {
        name: 'The Lodges',
        kind: 'Stay & play',
        description: 'Twenty-four timber lodges among the pines, each with its own terrace, fireplace and cart. Wake up five minutes from the first tee.',
        details: ['24 lodges', 'One to four bedrooms'],
        image: media.diningLodges,
        action: { label: 'Plan a stay', venue: 'The Lodges' }
    }
]

export const diningWords = [
    'Grown on the estate',
    'Landed this morning',
    'Poured from the valley',
    'Cooked over vine wood'
]

export const clubEvents: ClubEvent[] = [
    {
        name: 'Autumn Invitational',
        day: '17–19',
        month: 'Oct',
        year: '2026',
        format: '54-hole stroke play for members and guests',
        access: 'Open',
        image: library.coursePanorama
    },
    {
        name: 'Founders’ Cup Final',
        day: '07',
        month: 'Nov',
        year: '2026',
        format: 'Match play final on the Championship 18',
        access: 'Members',
        image: library.readingThePutt
    },
    {
        name: 'Night Nine',
        day: '05',
        month: 'Dec',
        year: '2026',
        format: 'Floodlit team scramble on the Short Nine',
        access: 'Open',
        image: library.academyRange
    },
    {
        name: 'Winter Foursomes',
        day: '23',
        month: 'Jan',
        year: '2027',
        format: 'Alternate shot pairs, followed by lunch on the Terrace',
        access: 'Open',
        image: library.membersWalking
    },
    {
        name: 'Spring Pro-Am',
        day: '13–14',
        month: 'Mar',
        year: '2027',
        format: 'Teams of three playing alongside touring professionals',
        access: 'Invitational',
        image: library.ironAndBall
    },
    {
        name: 'The Morrow Amateur',
        day: '20–23',
        month: 'May',
        year: '2027',
        format: '72-hole amateur championship from the back tees',
        access: 'Open',
        image: library.coastPeninsula
    }
]

export const privateEventStats: Stat[] = [
    { value: 240, label: 'guests seated in the Pavilion' },
    { value: 18, label: 'holes for a shotgun start' },
    { value: 24, label: 'lodges for overnight guests' }
]

export const natureFeatures: NatureFeature[] = [
    {
        title: 'The Reservoir',
        description: 'Cold-water swims from the jetty at sunrise, and paddleboards whenever the wind drops.',
        image: media.natureReservoir
    },
    {
        title: 'Pine Trails',
        description: 'Thirty-one kilometres of marked trails for walking, running and cycling, mapped in the member app.',
        image: media.natureTrails
    },
    {
        title: 'The Beach',
        description: 'A twelve-minute boardwalk through the dunes to four kilometres of sand you will usually have to yourself.',
        image: media.natureBeach
    }
]

export const natureStats: Stat[] = [
    { value: 142, suffix: ' ha', label: 'of heathland restored since 2021' },
    { value: 86, label: 'bird species recorded on the estate' },
    { value: 100, suffix: '%', label: 'of irrigation from recycled water' },
    { value: 31, suffix: ' km', label: 'of trails through the pines' }
]

/** Every photograph in the gallery, grouped for the filters on the gallery page. */
export const galleryCollection: GalleryItem[] = [
    { ...library.coastPeninsula, caption: 'The coast holes on the peninsula at sunset', shape: 'wide', category: 'Course' },
    { ...library.readingThePutt, caption: 'Reading the last putt of the day', shape: 'landscape', category: 'Academy' },
    { ...library.holeMirror, caption: 'Hole 16, Mirror, before the first group', shape: 'portrait', category: 'Course' },
    { ...library.clubhouseCarts, caption: 'The clubhouse and the cart court at golden hour', shape: 'landscape', category: 'Clubhouse' },
    { ...library.coursePanorama, caption: 'Greens and bunkers among the umbrella pines', shape: 'wide', category: 'Course' },
    { ...library.firstLight, caption: 'First light on the opening fairway', shape: 'portrait', category: 'Lifestyle' },
    { ...library.ironAndBall, caption: 'Last light on the practice tee', shape: 'square', category: 'Academy' },
    { ...library.cliffTerrace, caption: 'The terrace on the cliff above the coast holes', shape: 'wide', category: 'Clubhouse' },
    { ...library.holeQuarry, caption: 'Stone at the old quarry, hole 7', shape: 'portrait', category: 'Course' },
    { ...library.membersWalking, caption: 'Walking in after the final putt', shape: 'landscape', category: 'Lifestyle' },
    { ...library.lagoonHoles, caption: 'The lagoon holes at dusk', shape: 'wide', category: 'Course' },
    { ...library.academyRange, caption: 'The range bays under lights', shape: 'portrait', category: 'Academy' },
    { ...library.terraceDining, caption: 'Dinner on the Terrace', shape: 'landscape', category: 'Clubhouse' },
    { ...library.greenAtSunset, caption: 'The 3rd green as the sun drops', shape: 'landscape', category: 'Course' },
    { ...library.holeHeath, caption: 'Heath above the ocean, hole 11', shape: 'portrait', category: 'Course' },
    { ...library.clubhouseArrival, caption: 'Arriving for a twilight round', shape: 'landscape', category: 'Lifestyle' },
    { ...library.fairwayPanorama, caption: 'The lake fairway toward the old villa', shape: 'wide', category: 'Course' },
    { ...library.holeReturn, caption: 'The clubhouse above the 18th green', shape: 'portrait', category: 'Clubhouse' },
    { ...library.aerialCoast, caption: 'The cliff holes from directly above', shape: 'landscape', category: 'Course' },
    { ...library.twoGolfers, caption: 'An early start on the back nine', shape: 'landscape', category: 'Lifestyle' },
    { ...library.holePines, caption: 'The pine corridor on hole 4', shape: 'portrait', category: 'Course' },
    { ...library.goldenCourse, caption: 'Dune and pine along the front nine', shape: 'wide', category: 'Course' },
    { ...library.platedScallop, caption: 'Lunch on the Terrace', shape: 'square', category: 'Lifestyle' }
]

export const galleryCategories = ['All', 'Course', 'Clubhouse', 'Academy', 'Lifestyle']

/** The two drifting rows on the home page. */
export const galleryRows: GalleryItem[][] = [
    [
        { ...library.coursePanorama, caption: 'Greens and bunkers among the umbrella pines', shape: 'wide' },
        { ...library.readingThePutt, caption: 'Reading the last putt of the day', shape: 'landscape' },
        { ...library.holeMirror, caption: 'Hole 16, Mirror, before the first group', shape: 'portrait' },
        { ...library.clubhouseCarts, caption: 'The clubhouse and the cart court at golden hour', shape: 'landscape' },
        { ...library.fairwayPanorama, caption: 'The lake fairway toward the old villa', shape: 'wide' },
        { ...library.greenAtSunset, caption: 'The 3rd green as the sun drops', shape: 'square' }
    ],
    [
        { ...library.cliffTerrace, caption: 'The terrace on the cliff above the coast holes', shape: 'wide' },
        { ...library.ironAndBall, caption: 'Last light on the practice tee', shape: 'square' },
        { ...library.firstLight, caption: 'First light on the opening fairway', shape: 'portrait' },
        { ...library.terraceDining, caption: 'Dinner on the Terrace', shape: 'landscape' },
        { ...library.holeHeath, caption: 'Heath above the ocean, hole 11', shape: 'portrait' },
        { ...library.aerialCoast, caption: 'The cliff holes from directly above', shape: 'landscape' }
    ]
]
